import type { DriveItem } from "../../hooks/drive/driveTypes";
import { mimeTypeFromAssetPath } from "../../config/assets";

export const LOGOS_AND_BANNERS_FOLDER = "Logos and banners";
export const LOGOS_AND_BANNERS_ID = "logos-and-banners";

type CircleImages = { circleId: string; name: string; logo?: string; orgBanner?: string };

export type LogosAndBannersInput = {
  schoolId?: string;
  schoolName?: string;
  schoolLogo?: string;
  orgBanner?: string;
  circles?: CircleImages[];
  /**
   * Added to each address. A replaced logo or banner is saved at the same
   * address, so without this the browser keeps showing its old copy.
   */
  version?: string | number;
};

const isRealImage = (url: string | undefined): url is string =>
  Boolean(url && url.trim() && !url.includes("placeholder") && !url.startsWith("data:"));

const withVersion = (url: string, version: string | number | undefined) =>
  version === undefined ? url : `${url}${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(String(version))}`;

const extensionOf = (url: string) => {
  const path = url.split(/[?#]/)[0];
  const ext = path.includes(".") ? path.split(".").pop()!.toLowerCase() : "";
  return /^[a-z0-9]{2,5}$/.test(ext) ? ext : "png";
};

/**
 * The Organization's and each Circle's current logo and banner, as read-only
 * Drive files in one "Logos and banners" folder. They live in the logo store,
 * not on a Notice, so they can be viewed and picked but not moved or deleted.
 */
export function logosAndBanners({ schoolId, schoolName, schoolLogo, orgBanner, circles = [], version }: LogosAndBannersInput): DriveItem[] {
  // The Organization's own Circle comes fresh from the server, while the signed-in
  // user's saved details can be out of date, so its images win and it's listed once.
  const ownCircle = schoolId ? circles.find(circle => circle.circleId === schoolId) : undefined;
  const owners = [
    {
      id: schoolId || "",
      name: schoolName || "Organization",
      logo: isRealImage(ownCircle?.logo) ? ownCircle?.logo : schoolLogo,
      banner: isRealImage(ownCircle?.orgBanner) ? ownCircle?.orgBanner : orgBanner,
    },
    ...circles
      .filter(circle => circle !== ownCircle)
      .map(circle => ({ id: circle.circleId, name: circle.name, logo: circle.logo, banner: circle.orgBanner })),
  ];
  const seen = new Set<string>();
  const items: DriveItem[] = [];

  owners.forEach(owner => {
    ([["logo", owner.logo], ["banner", owner.banner]] as const).forEach(([kind, url]) => {
      if (!isRealImage(url) || seen.has(url)) return;
      seen.add(url);
      const ext = extensionOf(url);
      const safeName = owner.name.replace(/[\\/]/g, "-").trim() || "Circle";
      items.push({
        attachment: { name: `${LOGOS_AND_BANNERS_FOLDER}/${safeName} ${kind}.${ext}`, url: withVersion(url, version), type: mimeTypeFromAssetPath(`x.${ext}`), size: 0 },
        noticeId: LOGOS_AND_BANNERS_ID,
        noticeTitle: LOGOS_AND_BANNERS_FOLDER,
        circleId: owner.id,
        createdAt: new Date(0).toISOString(),
      });
    });
  });

  return items;
}
